// License plate matching for the live view search.
//
// Weighted Damerau-Levenshtein (optimal string alignment):
// - case, spaces and dashes are ignored ("abc-123" = "ABC123")
// - characters cameras / OCR mix up are cheap substitutions (0/O/D/Q, 1/I/L, 8/B, 5/S, 2/Z, 6/G)
// - swapping two neighbours counts as one cheap edit
// - for Hungarian plates (ABC123, AABB123) a letter typed where the plate has a digit
//   (or the other way round) costs a bit more than a normal typo
// Two modes are combined: the whole plate vs. the query, and the query as a part of the plate
// (so "HT41" finds "AEHT417"). The score is 0..1, 1 = exact.

const CONFUSABLE_GROUPS = ['0ODQ', '1IL', '8B', '5S', '2Z', '6G'];
const CONFUSABLE = new Map();
CONFUSABLE_GROUPS.forEach((group) => {
    [...group].forEach((a) => [...group].forEach((b) => a !== b && CONFUSABLE.set(a + b, true)));
});

const COST_CONFUSABLE = 0.3;
const COST_TRANSPOSE = 0.6;
const COST_WRONG_TYPE = 1.2;
const PARTIAL_RANK_PENALTY = 0.02;

export const normalizePlate = (s) =>
    String(s || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');

const isDigit = (c) => c >= '0' && c <= '9';

// expected character type per position for Hungarian formats, or null when the plate is something else
function hungarianLayout(plate) {
    if (/^[A-Z]{3}[0-9]{3}$/.test(plate)) return 'LLLDDD';
    if (/^[A-Z]{4}[0-9]{3}$/.test(plate)) return 'LLLLDDD';
    return null;
}

function substitutionCost(q, t, layoutType) {
    if (q === t) return 0;
    if (CONFUSABLE.has(q + t)) return COST_CONFUSABLE;
    if (layoutType && (layoutType === 'D') !== isDigit(q)) return COST_WRONG_TYPE;
    return 1;
}

// partial = true: the query may match any part of the text (free start / end)
function distance(q, t, layout, partial) {
    const n = q.length;
    const m = t.length;
    const d = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 1; i <= n; i += 1) d[i][0] = i;
    for (let j = 1; j <= m; j += 1) d[0][j] = partial ? 0 : j;
    for (let i = 1; i <= n; i += 1) {
        for (let j = 1; j <= m; j += 1) {
            let best = Math.min(
                d[i - 1][j] + 1,
                d[i][j - 1] + 1,
                d[i - 1][j - 1] + substitutionCost(q[i - 1], t[j - 1], layout && layout[j - 1])
            );
            if (
                i > 1 &&
                j > 1 &&
                q[i - 1] === t[j - 2] &&
                q[i - 2] === t[j - 1] &&
                q[i - 1] !== q[i - 2]
            ) {
                best = Math.min(best, d[i - 2][j - 2] + COST_TRANSPOSE);
            }
            d[i][j] = best;
        }
    }
    return partial ? Math.min(...d[n]) : d[n][m];
}

/**
 * How well the query matches a plate.
 * @returns {{score: number, rank: number}} score 0..1 for display, rank for sorting (full matches first)
 */
export function matchPlate(query, plateText) {
    const q = normalizePlate(query);
    const t = normalizePlate(plateText);
    if (!q) return { score: 1, rank: 1 };
    if (!t) return { score: 0, rank: 0 };
    const layout = hungarianLayout(t);
    const full = Math.max(0, 1 - distance(q, t, layout, false) / Math.max(q.length, t.length));
    const part = q.length < t.length ? Math.max(0, 1 - distance(q, t, layout, true) / q.length) : 0;
    const score = Math.max(full, part);
    return { score, rank: score - (part > full ? PARTIAL_RANK_PENALTY : 0) };
}

// best-scoring candidate text for a plate (used to verify OCR results)
export function bestMatch(plateText, candidates) {
    return candidates
        .map((c) => ({ text: c, ...matchPlate(c, plateText) }))
        .sort((a, b) => b.rank - a.rank)[0];
}

// does an OCR'd string look like a plate at all?
export const looksLikePlate = (text) => {
    const n = normalizePlate(text);
    return n.length >= 5 && n.length <= 9 && /[A-Z]/.test(n) && /[0-9]/.test(n);
};
